import * as React from 'react';
import styles from './index.less';

export interface RssSyncProgressData {
  status?: string;
  /** false：首次导入尚未成功完成，单品管理页应展示大卡片导入 UI */
  fullImportCompleted?: boolean;
  failedItems?: { itemTitle?: string; errorMessage?: string }[];
}

export type RssSyncBannerState =
  | { kind: 'none' }
  | { kind: 'importing' }
  | { kind: 'multiple' }
  | { kind: 'sync_failed' }
  | { kind: 'single_item'; itemTitle: string; errorMessage: string };

// 根据同步进度接口返回，推导应展示的横幅：
// 接口未返回（data 为空）：不展示
// 有 failedItems 时只按单品错误计数（不再叠加 sync-level，避免顶栏/下方重复）
// 无 failedItems 且 status 为 partial_failed/failed：同步级失败
// 0 个错误：进行中或全部成功，不展示异常横幅
// 1 个错误：同步级失败展示「自动同步异常」；单个单品失败展示「{itemTitle} 发行失败：{errorMessage}」
// 2 个及以上：展示「RSS 订阅信息存在多项异常 + 查看」
export function resolveRssSyncBannerState(
  data: RssSyncProgressData | null | undefined,
): RssSyncBannerState {
  // 接口尚未返回时不展示顶部提示栏
  if (data == null) {
    return { kind: 'none' };
  }

  const status = data.status ?? '';
  const failedItems = data.failedItems ?? [];

  if (status === '' || status === 'pending' || status === 'running') {
    return { kind: 'importing' };
  }

  if (failedItems.length >= 2) {
    return { kind: 'multiple' };
  }
  if (failedItems.length === 1) {
    return {
      kind: 'single_item',
      itemTitle: failedItems[0].itemTitle ?? '',
      errorMessage: failedItems[0].errorMessage ?? '',
    };
  }
  if (status === 'partial_failed' || status === 'failed') {
    return { kind: 'sync_failed' };
  }
  return { kind: 'none' };
}

// 多项异常弹窗：仅列出各单品失败（同步级文案已由顶栏展示，避免重复）
export function buildRssSyncErrorMessages(
  data: RssSyncProgressData | null | undefined,
): string[] {
  const failedItems = data?.failedItems ?? [];
  return failedItems.map(
    (item) => `${item.itemTitle ?? ''} 发行失败：${item.errorMessage ?? ''}`,
  );
}

export interface FPodcastRssSyncErrorBannerProps {
  mode: 'multiple' | 'sync_failed' | 'single_item';
  itemTitle?: string;
  errorMessage?: string;
  className?: string;
  style?: React.CSSProperties;
  onView?: () => void;
}

function FPodcastRssSyncErrorBanner({
  mode,
  itemTitle = '',
  errorMessage = '',
  className = '',
  style,
  onView,
}: FPodcastRssSyncErrorBannerProps) {
  return (
    <div className={[styles.banner, className].join(' ')} style={style}>
      {mode === 'multiple' && (
        <>
          <span className={styles.text}>RSS 订阅信息存在多项异常</span>
          <span
            className={styles.link}
            style={{ cursor: onView ? 'pointer' : 'default' }}
            onClick={onView}
          >
            查看
          </span>
        </>
      )}
      {mode === 'sync_failed' && (
        <span className={styles.text}>自动同步异常，请检查您的 RSS 订阅地址并手动同步。</span>
      )}
      {mode === 'single_item' && (
        <span className={styles.text}>{`${itemTitle} 发行失败：${errorMessage}`}</span>
      )}
    </div>
  );
}

export default FPodcastRssSyncErrorBanner;
